/**
 * Drafts waiting to be sent.
 *
 * Rules drop messages here, as does "Draft emails" on a selection. Each one is
 * editable before it goes; sending hands off to mail or WhatsApp and clears
 * the draft.
 */
import { useApp } from '../store/AppContext.jsx';
import { formatDate } from '../lib/dates.js';
import { digitsOf, whatsappLink } from '../lib/format.js';
import Icon from '../lib/icons.jsx';
import { Card, CardHead, CardBody, EmptyState, Avatar } from '../components/Primitives.jsx';

export default function Outbox() {
  const { S, rows, setOutbox, clearOutbox, patchClient, openDraft, toast } = useApp();
  const list = rows.filter(r => r.outbox);

  if (!list.length) {
    return (
      <Card>
        <EmptyState icon="check" tone="g-green" title="Outbox is empty">
          Rules drop drafts here, or select clients and choose “Draft emails”.
        </EmptyState>
      </Card>
    );
  }

  const copy = (text, msg = 'Copied') => {
    if (!navigator.clipboard) { toast('Clipboard unavailable'); return; }
    navigator.clipboard.writeText(text).then(() => toast(msg), () => toast('Copy failed'));
  };

  /* mailto: goes to the operating system's default mail program, which on
     Windows is usually Outlook. Gmail is a website and needs its own URL. */
  const handOff = (r, url) => {
    window.open(url, '_blank', 'noopener');
    patchClient(r.id, { lastContacted: formatDate(new Date(), false, S.dateFmt) });
    setOutbox(r.id, null);
  };

  const sendMail = r => handOff(r,
    `mailto:${encodeURIComponent(r.email || '')}`
    + `?subject=${encodeURIComponent(r.outbox.subject || '')}`
    + `&body=${encodeURIComponent(r.outbox.body || '')}`);

  const sendGmail = r => handOff(r,
    'https://mail.google.com/mail/?view=cm&fs=1'
    + `&to=${encodeURIComponent(r.email || '')}`
    + `&su=${encodeURIComponent(r.outbox.subject || '')}`
    + `&body=${encodeURIComponent(r.outbox.body || '')}`);

  const sendWhatsApp = r => {
    if (!digitsOf(r.phone)) { toast('No phone number on this client'); return; }
    window.open(whatsappLink(r.phone, r.outbox.body), '_blank');
    patchClient(r.id, { lastContacted: formatDate(new Date(), false, S.dateFmt) });
    setOutbox(r.id, null);
  };

  const copyAll = () => {
    const text = list.map(r =>
      `— ${r.name} (${r.email || r.phone || ''})\n`
      + (r.outbox.subject ? `Subject: ${r.outbox.subject}\n` : '')
      + r.outbox.body).join('\n\n');
    copy(text, 'All drafts copied');
  };

  const discardAll = () => {
    // eslint-disable-next-line no-alert
    if (window.confirm('Discard every draft in the outbox?')) clearOutbox();
  };

  return (
    <Card>
      <CardHead icon="card" tone="g-orange" title="Ready to send" count={list.length}>
        <button type="button" className="btn sm" onClick={copyAll}>Copy all</button>
        <button type="button" className="btn sm danger" onClick={discardAll}>Discard all</button>
      </CardHead>

      <CardBody>
        {list.map(r => {
          const o = r.outbox;
          return (
            <div className="ob" key={r.id}>
              <div className="obh">
                <Avatar name={r.name} />
                <div style={{ flex: 1, minWidth: 0 }}>
                  <b>{r.name}</b>
                  <div className="sub">
                    {r.contact || r.email || ''}{r.phone ? ` · ${r.phone}` : ''}
                  </div>
                </div>
                <span className={`chan ${o.channel}`}>{o.channel}</span>
                <span className="chan rule">{o.why || 'Manual'}</span>
              </div>

              {o.channel === 'email' ? (
                <input
                  placeholder="Subject"
                  value={o.subject || ''}
                  onChange={e => setOutbox(r.id, { ...o, subject: e.target.value })}
                />
              ) : null}

              <textarea
                value={o.body || ''}
                onChange={e => setOutbox(r.id, { ...o, body: e.target.value })}
              />

              <div className="obacts">
                {o.channel === 'whatsapp' ? (
                  <button type="button" className="btn primary sm" onClick={() => sendWhatsApp(r)}>
                    <Icon name="phone" />Send on WhatsApp
                  </button>
                ) : (
                  <>
                    <button type="button" className="btn primary sm" onClick={() => sendGmail(r)}>
                      <Icon name="mail" />Gmail
                    </button>
                    <button type="button" className="btn sm" onClick={() => sendMail(r)}>
                      Mail app
                    </button>
                  </>
                )}
                <button
                  type="button"
                  className="btn sm"
                  onClick={() => copy((o.subject ? `Subject: ${o.subject}\n\n` : '') + o.body)}
                >
                  Copy
                </button>
                <button
                  type="button"
                  className="btn sm"
                  onClick={() => openDraft({ kind: 'client', id: r.id, channel: o.channel })}
                >
                  Rewrite
                </button>
                <button
                  type="button"
                  className="btn sm"
                  onClick={() => setOutbox(r.id, { ...o, channel: o.channel === 'email' ? 'whatsapp' : 'email' })}
                >
                  Switch channel
                </button>
                <button type="button" className="btn sm danger" onClick={() => setOutbox(r.id, null)}>
                  Discard
                </button>
              </div>
            </div>
          );
        })}
      </CardBody>
    </Card>
  );
}
